/**
 * The visual theme axis (Classic | Lilac), independent of light/dark.
 *
 * Stored per browser in localStorage and applied as `data-theme` on <html>. The inline script
 * in index.html reads the same key before first paint so there is no flash; this module keeps
 * it in sync afterwards (ThemeProvider). Per-user persistence is GRA-142.
 */

export const UI_THEMES = ["classic", "lilac"] as const;
export type UiTheme = (typeof UI_THEMES)[number];

export const UI_THEME_KEY = "graftus_ui_theme";
/** Lilac is the default since the platform redesign (decision 2026-10-27); Classic stays selectable. */
export const DEFAULT_UI_THEME: UiTheme = "lilac";

export function isUiTheme(value: unknown): value is UiTheme {
  return typeof value === "string" && (UI_THEMES as readonly string[]).includes(value);
}

/** The stored theme, or the default when nothing valid is stored or storage is unavailable. */
export function readUiTheme(storage: Pick<Storage, "getItem"> | null = safeStorage()): UiTheme {
  try {
    const stored = storage?.getItem(UI_THEME_KEY);
    return isUiTheme(stored) ? stored : DEFAULT_UI_THEME;
  } catch {
    return DEFAULT_UI_THEME;
  }
}

export function applyUiTheme(theme: UiTheme, root: HTMLElement = document.documentElement): void {
  root.dataset.theme = theme;
}

export function storeUiTheme(
  theme: UiTheme,
  storage: Pick<Storage, "setItem"> | null = safeStorage(),
): void {
  try {
    storage?.setItem(UI_THEME_KEY, theme);
  } catch {
    // Private mode or blocked storage: the choice lives for this page view only.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}
